import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AddIcon from '@mui/icons-material/Add'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { formatDate } from '../../format-date'
import { useBillingApi } from '../../api/billing/billing-context'
import {
    ApplianceLicenseAllocationResult,
    ApplianceLicenseInventoryItem,
    ApplianceLicenseProduct,
    ApplianceLicenseProductCode,
    ApplianceLicensePurchase,
    Customer,
    Role,
} from 'common-billing-server/types'
import { isRoleAuthorized } from 'common-billing-server/role'
import {
    formatApplianceLicensePurchaseCsv,
    licenseTransferLabel,
    listAllPurchasePages,
} from 'common-billing-server/purchaseCsv'
import { downloadCsv } from 'common-ui/utils/downloadCsv'
import { makeCustomerAppliancesPath } from '../../routes/path'
import { Banner } from 'common-ui/components/Banner'
import { ActivationKey } from '../common/ActivationKey'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { useAuth } from '../login/Auth'
import { TransferLicensesDialog, TransferSource } from './TransferLicensesDialog'

interface CustomerApplianceLicenseManagementProps {
    customerId: string
    customerName: string
}

export const CustomerApplianceLicenseManagement = ({
    customerId,
    customerName,
}: CustomerApplianceLicenseManagementProps) => {
    const billingApi = useBillingApi()
    const { user } = useAuth()
    const canTransfer = !!user && isRoleAuthorized({ role: user.role, minimumAllowedRole: Role.licenseAdmin })
    const [transferSource, setTransferSource] = useState<TransferSource | null>(null)
    const [inventory, setInventory] = useState<ApplianceLicenseInventoryItem[]>([])
    const [products, setProducts] = useState<ApplianceLicenseProduct[]>([])
    const [purchases, setPurchases] = useState<ApplianceLicensePurchase[]>([])
    const [purchaseTotal, setPurchaseTotal] = useState(0)
    const [purchasesCustomerId, setPurchasesCustomerId] = useState<string>()
    const [isLoading, setIsLoading] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const currentCustomerId = useRef(customerId)
    currentCustomerId.current = customerId
    const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false)
    const [isAllocationDialogOpen, setIsAllocationDialogOpen] = useState(false)
    const [selectedProductCode, setSelectedProductCode] = useState<ApplianceLicenseProductCode | ''>('')
    const [purchaseQuantity, setPurchaseQuantity] = useState(1)
    const [serialNumber, setSerialNumber] = useState('')
    const [isSubmittingPurchase, setIsSubmittingPurchase] = useState(false)
    const [isSubmittingAllocation, setIsSubmittingAllocation] = useState(false)
    const [latestAllocation, setLatestAllocation] = useState<ApplianceLicenseAllocationResult | null>(null)
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()
    const [notice, setNotice] = useState<string>()

    const refreshData = async () => {
        const operationId = 'fetch-appliance-licenses'
        removeFailedOperation(operationId)
        setIsLoading(true)
        try {
            const [inventoryResult, productResult, purchaseResult] = await Promise.all([
                billingApi.listApplianceLicenseInventories({ filter: { customerId } }),
                billingApi.listApplianceLicenseProducts(),
                billingApi.listApplianceLicensePurchases({ filter: { customerId }, limit: 500 }),
            ])
            if (currentCustomerId.current !== customerId) return
            setInventory(inventoryResult.items)
            setProducts(productResult.items)
            setPurchases(purchaseResult.items)
            setPurchaseTotal(purchaseResult.total)
            setPurchasesCustomerId(customerId)
        } catch (error) {
            if (currentCustomerId.current !== customerId) return
            addFailedOperation({
                id: operationId,
                message: 'Failed to fetch appliance license data',
                error,
            })
        }
        if (currentCustomerId.current === customerId) setIsLoading(false)
    }

    useEffect(() => {
        void refreshData()
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId])

    useEffect(() => {
        if (!selectedProductCode && products.length > 0) {
            setSelectedProductCode(products[0].code)
        }
    }, [products, selectedProductCode])

    const resetPurchaseDialog = () => {
        setSelectedProductCode(products[0]?.code ?? '')
        setPurchaseQuantity(1)
        setIsPurchaseDialogOpen(false)
    }

    const resetAllocationDialog = () => {
        setSelectedProductCode(products[0]?.code ?? '')
        setSerialNumber('')
        setIsAllocationDialogOpen(false)
    }

    const handleCreatePurchase = async () => {
        const operationId = 'create-appliance-license-purchase'
        removeFailedOperation(operationId)
        setIsSubmittingPurchase(true)
        try {
            await billingApi.createApplianceLicensePurchase({
                customerId,
                productCode: selectedProductCode as ApplianceLicenseProductCode,
                quantity: purchaseQuantity,
            })
            setNotice(`Added ${purchaseQuantity} ${productByCode[selectedProductCode]?.name ?? selectedProductCode}.`)
            resetPurchaseDialog()
            await refreshData()
        } catch (error) {
            addFailedOperation({
                id: operationId,
                message: 'Failed to add appliance license inventory',
                error,
            })
        }
        setIsSubmittingPurchase(false)
    }

    const handleAllocateLicense = async () => {
        const operationId = 'allocate-appliance-license'
        removeFailedOperation(operationId)
        setIsSubmittingAllocation(true)
        try {
            const result = await billingApi.createApplianceLicenseAllocation({
                customerId,
                productCode: selectedProductCode as ApplianceLicenseProductCode,
                serialNumber: serialNumber.trim(),
            })
            if (result.bundle) {
                setLatestAllocation(result)
                setNotice(`Updated the license bundle for ${result.bundle.serialNumber}.`)
            } else {
                setLatestAllocation(null)
                setNotice(
                    `Allocated ${productByCode[result.allocation.productCode]?.name ?? result.allocation.productCode} to ${result.allocation.serialNumber}.`
                )
            }
            resetAllocationDialog()
            await refreshData()
        } catch (error) {
            addFailedOperation({
                id: operationId,
                message: 'Failed to allocate appliance license',
                error,
            })
        }
        setIsSubmittingAllocation(false)
    }

    const productByCode: Record<string, ApplianceLicenseProduct> = Object.fromEntries(
        products.map((product) => [product.code, product])
    )
    const productNames = Object.fromEntries(products.map((product) => [product.code, product.name]))
    const availableProductCodes = new Set(
        inventory.filter((item) => item.available > 0).map((item) => item.productCode)
    )
    const visiblePurchases = purchasesCustomerId === customerId ? purchases : []
    const visiblePurchaseTotal = purchasesCustomerId === customerId ? purchaseTotal : 0

    const handleTransfer = async (source: TransferSource, recipient: Customer, quantity: number) => {
        const operationId = 'transfer-appliance-licenses'
        removeFailedOperation(operationId)
        try {
            await billingApi.transferApplianceLicensePurchase(source.id, { customerId: recipient.id, quantity })
            setNotice(`Transferred ${quantity} ${source.name} to ${recipient.name}.`)
            await refreshData()
            return true
        } catch (error) {
            addFailedOperation({ id: operationId, message: 'Failed to transfer appliance licenses', error })
            return false
        }
    }

    const handleExportPurchases = async () => {
        const operationId = 'export-appliance-license-purchases'
        removeFailedOperation(operationId)
        setIsExporting(true)
        try {
            const allPurchases = await listAllPurchasePages(async (skip, limit) => {
                if (currentCustomerId.current !== customerId) throw new Error('Customer changed during export.')
                const page = await billingApi.listApplianceLicensePurchases({ filter: { customerId }, skip, limit })
                if (currentCustomerId.current !== customerId) throw new Error('Customer changed during export.')
                return page
            })
            if (currentCustomerId.current !== customerId) throw new Error('Customer changed during export.')
            downloadCsv(
                `appliance-license-purchases-${customerId}.csv`,
                formatApplianceLicensePurchaseCsv(allPurchases, productNames, 'billing')
            )
        } catch (error) {
            addFailedOperation({ id: operationId, message: 'Failed to export appliance license transactions', error })
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <Paper sx={{ mt: 2, padding: '20px' }}>
            <Box
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}
            >
                <Typography variant="h6">Appliance Licenses</Typography>
                <Box style={{ display: 'flex', gap: '12px' }}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setIsPurchaseDialogOpen(true)}
                        disabled={isLoading}
                    >
                        Add inventory
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<VpnKeyIcon />}
                        onClick={() => setIsAllocationDialogOpen(true)}
                        disabled={isLoading}
                    >
                        Allocate to serial
                    </Button>
                </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" style={{ marginBottom: '16px' }}>
                Inventory is purchased unbound and allocated permanently to appliance serial numbers.
            </Typography>

            {errorBanner}
            {notice && <Banner id="notice" isOpen={true} message={notice} onClose={() => setNotice(undefined)} />}

            {latestAllocation?.bundle ? (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                            Updated license bundle (v{latestAllocation.bundle.version}) for{' '}
                            {latestAllocation.bundle.serialNumber}. This single token carries every license on the
                            serial — a newer version supersedes any older one.
                        </Typography>
                        <Button size="small" onClick={() => setLatestAllocation(null)}>
                            Dismiss
                        </Button>
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={latestAllocation.bundle.token}
                        label="Signed appliance license bundle"
                        slotProps={{ htmlInput: { readOnly: true } }}
                    />
                    {latestAllocation.allocation.activationKey ? (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                Enter this activation key in the appliance&apos;s Standalone page to unlock the
                                software.
                            </Typography>
                            <ActivationKey value={latestAllocation.allocation.activationKey} />
                        </Box>
                    ) : null}
                </Paper>
            ) : null}

            <Typography variant="subtitle1" style={{ marginBottom: '12px' }}>
                Inventory
            </Typography>

            {isLoading ? (
                <CircularProgress />
            ) : inventory.length === 0 ? (
                <Alert severity="info">No appliance license inventory has been added for this customer</Alert>
            ) : (
                <TableContainer style={{ marginBottom: '24px' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Product</TableCell>
                                <TableCell>Purchased</TableCell>
                                <TableCell>Allocated</TableCell>
                                <TableCell>Available</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {inventory.map((item) => (
                                <TableRow key={item.productCode}>
                                    <TableCell>{productByCode[item.productCode]?.name ?? item.productCode}</TableCell>
                                    <TableCell>{item.purchased}</TableCell>
                                    <TableCell>{item.allocated}</TableCell>
                                    <TableCell>{item.available}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="subtitle1">Transaction history ({visiblePurchaseTotal})</Typography>
                <Button
                    onClick={() => void handleExportPurchases()}
                    disabled={isLoading || isExporting || visiblePurchaseTotal === 0}
                >
                    {isExporting ? 'Exporting…' : 'Export CSV'}
                </Button>
            </Box>

            {isLoading ? (
                <CircularProgress />
            ) : visiblePurchases.length === 0 ? (
                <Alert severity="info">No license transactions have been recorded for this customer</Alert>
            ) : (
                <TableContainer style={{ marginBottom: '24px' }}>
                    <Table size="small" aria-label="Appliance license purchase records">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell align="right">Quantity</TableCell>
                                <TableCell>Purchase order</TableCell>
                                <TableCell>Origin</TableCell>
                                {canTransfer ? <TableCell>Actions</TableCell> : null}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visiblePurchases.map((purchase) => (
                                <TableRow key={purchase.id}>
                                    <TableCell>{formatDate(new Date(purchase.createdAt))}</TableCell>
                                    <TableCell>
                                        {productByCode[purchase.productCode]?.name ?? purchase.productCode}
                                    </TableCell>
                                    <TableCell align="right">{purchase.quantity}</TableCell>
                                    <TableCell>{purchase.netsuiteOrder?.purchaseOrderNumber ?? '—'}</TableCell>
                                    <TableCell>
                                        {purchase.transfer ? (
                                            <Typography variant="body2">
                                                {licenseTransferLabel(purchase.transfer, 'billing')}
                                            </Typography>
                                        ) : purchase.netsuiteOrder ? (
                                            <Typography variant="body2">
                                                Sales order {purchase.netsuiteOrder.salesOrderNumber}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2">Added in billing server</Typography>
                                        )}
                                    </TableCell>
                                    {canTransfer ? (
                                        <TableCell>
                                            {purchase.transferable ? (
                                                <IconButton
                                                    aria-label={`Transfer ${productNames[purchase.productCode] ?? purchase.productCode}`}
                                                    title="Transfer unallocated licenses"
                                                    size="small"
                                                    onClick={() =>
                                                        setTransferSource({
                                                            id: purchase.id,
                                                            name:
                                                                productNames[purchase.productCode] ??
                                                                purchase.productCode,
                                                            transferable: purchase.transferable ?? 0,
                                                        })
                                                    }
                                                >
                                                    <SwapHorizIcon />
                                                </IconButton>
                                            ) : null}
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {visiblePurchaseTotal > visiblePurchases.length ? (
                <Typography variant="body2" color="text.secondary">
                    Showing the latest {visiblePurchases.length} transactions. Export CSV includes the full history.
                </Typography>
            ) : null}

            <Button
                component={RouterLink}
                to={makeCustomerAppliancesPath(customerId)}
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                sx={{ mt: 2 }}
            >
                View customer appliances
            </Button>

            <TransferLicensesDialog
                source={transferSource}
                customerId={customerId}
                customerName={customerName}
                onClose={() => setTransferSource(null)}
                onTransfer={handleTransfer}
            />

            <Dialog open={isPurchaseDialogOpen} onClose={resetPurchaseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Add appliance license inventory</DialogTitle>
                <DialogContent>
                    {products.length === 0 && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            No license products are defined. A super user can create products under Global settings →
                            license products.
                        </Alert>
                    )}
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Product</InputLabel>
                        <Select
                            value={selectedProductCode}
                            onChange={(e) => setSelectedProductCode(e.target.value)}
                            label="Product"
                        >
                            {products.map((product) => (
                                <MenuItem key={product.code} value={product.code}>
                                    {product.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        type="number"
                        label="Quantity"
                        value={purchaseQuantity}
                        onChange={(e) => setPurchaseQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                        margin="normal"
                        slotProps={{ htmlInput: { min: 1 } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={resetPurchaseDialog} disabled={isSubmittingPurchase}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleCreatePurchase()}
                        variant="contained"
                        disabled={isSubmittingPurchase || !selectedProductCode}
                    >
                        {isSubmittingPurchase ? <CircularProgress size={20} /> : 'Add inventory'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isAllocationDialogOpen} onClose={resetAllocationDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Allocate license to appliance serial</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" style={{ marginTop: '8px' }}>
                        Customer allocations are permanent. Replacement or re-host flows are handled through support.
                    </Alert>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Product</InputLabel>
                        <Select
                            value={selectedProductCode}
                            onChange={(e) => setSelectedProductCode(e.target.value)}
                            label="Product"
                        >
                            {products.map((product) => {
                                const available = inventory.find((item) => item.productCode === product.code)?.available
                                return (
                                    <MenuItem key={product.code} value={product.code} disabled={!available}>
                                        {product.name}
                                        {available ? ` (${available} available)` : ' (none available)'}
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Serial number"
                        value={serialNumber}
                        onChange={(e) => setSerialNumber(e.target.value)}
                        margin="normal"
                        helperText={
                            productByCode[selectedProductCode]?.licenseFamily === 'flow'
                                ? 'Billing-only flow coverage will be bound to this serial.'
                                : 'Manual entry for now. The signed token will only verify on this serial.'
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={resetAllocationDialog} disabled={isSubmittingAllocation}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleAllocateLicense()}
                        variant="contained"
                        disabled={
                            !serialNumber.trim() ||
                            isSubmittingAllocation ||
                            !selectedProductCode ||
                            !availableProductCodes.has(selectedProductCode)
                        }
                    >
                        {isSubmittingAllocation ? <CircularProgress size={20} /> : 'Allocate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
